<template>
  <section class="company-access-panel">
    <div class="company-access-panel__stack">
      <article class="company-access-panel__card company-access-panel__card--banner">
        <div class="company-access-panel__banner-copy">
          <p class="company-access-panel__eyebrow">Accesos</p>
          <h2 class="company-access-panel__title">Boveda de credenciales</h2>
          <p class="company-access-panel__description">
            Información sensible. Algunas acciones requieren permisos especiales.
          </p>
        </div>

        <div v-if="rotationAlerts.length" class="company-access-panel__alert">
          {{ rotationAlerts.length }} acceso(s) requieren revision o rotacion.
        </div>
      </article>

      <article class="company-access-panel__card">
        <div class="company-access-panel__card-head">
          <div>
            <p class="company-access-panel__eyebrow">Credenciales</p>
            <h3 class="company-access-panel__section-title">Secretos y accesos operativos</h3>
          </div>

          <div class="company-access-panel__head-actions">
            <button
              v-if="permissions.canRevealSecret"
              class="company-access-panel__ghost-action"
              type="button"
              @click="$emit('view-audit')"
            >
              Auditoria
            </button>

            <button
              v-if="permissions.canCreate"
              class="company-access-panel__primary-action"
              type="button"
              @click="$emit('create-secret')"
            >
              Nuevo acceso
            </button>
          </div>
        </div>

        <div v-if="loading" class="company-access-panel__empty">
          Cargando accesos...
        </div>

        <div v-else-if="secrets.length" class="company-access-panel__vault">
          <article
            v-for="secret in secrets"
            :key="secret.id"
            class="company-access-panel__secret-card"
          >
            <div class="company-access-panel__secret-main">
              <div class="company-access-panel__secret-top">
                <div>
                  <h4 class="company-access-panel__secret-name">
                    {{ secret.name || 'Acceso sin nombre' }}
                  </h4>

                  <p class="company-access-panel__secret-meta">
                    {{ secret.category || 'General' }}
                    <span v-if="secret.owner">· {{ secret.owner }}</span>
                  </p>
                </div>

                <span
                  v-if="secret.needs_rotation"
                  class="company-access-panel__flag company-access-panel__flag--warning"
                >
                  Rotacion pendiente
                </span>
              </div>

              <div class="company-access-panel__secret-field">
                <span class="company-access-panel__field-label">Usuario</span>
                <span class="company-access-panel__field-value">
                  {{ secret.username || 'No informado' }}
                </span>
              </div>

              <div class="company-access-panel__secret-field">
                <span class="company-access-panel__field-label">Valor</span>
                <span class="company-access-panel__field-value company-access-panel__field-value--masked">
                  {{ getMaskedValue(secret) }}
                </span>
              </div>

              <div class="company-access-panel__secret-footer">
                <div class="company-access-panel__secret-dates">
                  <span>Actualizado: {{ formatDate(secret.updated_at) }}</span>
                  <span v-if="secret.last_used_at">Ultimo uso: {{ formatDate(secret.last_used_at) }}</span>
                </div>

                <div class="company-access-panel__actions">
                  <button
                    v-if="permissions.canRevealSecret"
                    class="company-access-panel__ghost-action"
                    type="button"
                    @click="$emit('reveal-secret', secret)"
                  >
                    Revelar
                  </button>

                  <button
                    v-if="permissions.canRevealSecret"
                    class="company-access-panel__ghost-action"
                    type="button"
                    @click="$emit('copy-secret', secret)"
                  >
                    Copiar
                  </button>

                  <button
                    v-if="permissions.canEdit"
                    class="company-access-panel__ghost-action"
                    type="button"
                    @click="$emit('edit-secret', secret)"
                  >
                    Editar
                  </button>

                  <button
                    v-if="permissions.canDelete"
                    class="company-access-panel__danger-action"
                    type="button"
                    @click="$emit('delete-secret', secret)"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div v-else class="company-access-panel__empty">
          No hay accesos registrados.
        </div>
      </article>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CompanyAccessPanel',
  props: {
    secrets: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    permissions: {
      type: Object,
      required: true
    },
    rotationAlerts: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    getMaskedValue (secret) {
      if (secret.masked_value) return secret.masked_value
      if (secret.secret && typeof secret.secret === 'string') {
        return '•'.repeat(Math.max(8, Math.min(secret.secret.length, 14)))
      }
      return '••••••••'
    },
    formatDate (value) {
      return value ? new Date(value).toLocaleDateString() : 'Sin registro'
    }
  }
}
</script>

<style scoped>
.company-access-panel__stack {
  display: grid;
  gap: 1rem;
}

.company-access-panel__card {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.58);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(246, 239, 231, 0.94));
  box-shadow: 0 20px 50px rgba(43, 34, 24, 0.06);
}

.company-access-panel__card--banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background:
    radial-gradient(circle at top right, rgba(211, 198, 176, 0.5), transparent 38%),
    linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(243, 236, 227, 0.94));
}

.company-access-panel__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(58, 46, 33, 0.48);
}

.company-access-panel__title,
.company-access-panel__section-title {
  margin: 0;
  color: #211913;
}

.company-access-panel__title {
  font-size: 1.2rem;
}

.company-access-panel__section-title {
  font-size: 1rem;
}

.company-access-panel__description {
  margin: 0.4rem 0 0;
  color: rgba(45, 35, 26, 0.62);
}

.company-access-panel__alert {
  padding: 0.7rem 0.9rem;
  border-radius: 18px;
  background: rgba(184, 139, 59, 0.12);
  color: #8a6520;
  font-weight: 700;
  white-space: nowrap;
}

.company-access-panel__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.company-access-panel__head-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.company-access-panel__primary-action,
.company-access-panel__ghost-action,
.company-access-panel__danger-action {
  min-height: 2.4rem;
  padding: 0 0.85rem;
  border-radius: 999px;
}

.company-access-panel__primary-action {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

.company-access-panel__ghost-action {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.66);
  color: #392d22;
}

.company-access-panel__danger-action {
  border: 0;
  background: rgba(185, 92, 92, 0.14);
  color: #8a3f3f;
}

.company-access-panel__vault {
  display: grid;
  gap: 0.8rem;
}

.company-access-panel__secret-card {
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.6);
  padding: 0.95rem;
}

.company-access-panel__secret-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
  margin-bottom: 0.8rem;
}

.company-access-panel__secret-name {
  margin: 0;
  color: #241c16;
  font-size: 1rem;
}

.company-access-panel__secret-meta {
  margin: 0.2rem 0 0;
  color: rgba(45, 35, 26, 0.62);
  font-size: 0.88rem;
}

.company-access-panel__flag {
  display: inline-flex;
  align-items: center;
  min-height: 1.7rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.company-access-panel__flag--warning {
  background: rgba(184, 139, 59, 0.14);
  color: #8a6520;
}

.company-access-panel__secret-field {
  display: grid;
  gap: 0.18rem;
  margin-bottom: 0.65rem;
}

.company-access-panel__field-label {
  color: rgba(58, 46, 33, 0.5);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.company-access-panel__field-value {
  color: #241c16;
  font-weight: 600;
}

.company-access-panel__field-value--masked {
  letter-spacing: 0.12em;
}

.company-access-panel__secret-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.9rem;
}

.company-access-panel__secret-dates {
  display: grid;
  gap: 0.2rem;
  color: rgba(45, 35, 26, 0.58);
  font-size: 0.84rem;
}

.company-access-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.company-access-panel__empty {
  color: rgba(45, 35, 26, 0.58);
}

@media (max-width: 700px) {
  .company-access-panel__card--banner,
  .company-access-panel__card-head,
  .company-access-panel__secret-top,
  .company-access-panel__secret-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
